import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BillingManagement = ({ currentPlan, pricingPlans }) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Mock current usage data
  const currentUsage = {
    jobPostings: { used: 3, limit: 5 },
    activeJobs: { used: 2, limit: 3 },
    candidateViews: { used: 67, limit: 100 }
  };

  const currentPlanData = pricingPlans?.find((plan) => plan?.id === currentPlan);

  const getUsagePercentage = (used, limit) => {
    if (limit === 'unlimited') return 0;
    return (used / limit) * 100;
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 70) return 'bg-warning';
    return 'bg-success';
  };

  const formatLimit = (limit) => {
    return typeof limit === 'number' ? limit : '∞';
  };

  const paymentMethods = [
    {
      id: 1,
      type: 'card',
      last4: '4242',
      brand: 'Visa',
      expiry: '12/25',
      isDefault: true
    }
  ];

  const billingHistory = [
    {
      id: 1,
      date: '2026-01-03',
      amount: 49.0,
      status: 'paid',
      invoice: 'INV-2026-001'
    },
    {
      id: 2,
      date: '2025-12-03',
      amount: 49.0,
      status: 'paid',
      invoice: 'INV-2025-012'
    },
    {
      id: 3,
      date: '2025-11-03',
      amount: 49.0,
      status: 'paid',
      invoice: 'INV-2025-011'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Current Subscription Card */}
      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">
              Current Subscription
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-primary">
                {currentPlanData?.name} Plan
              </span>
              <span className="inline-flex items-center gap-1 bg-success/10 text-success px-3 py-1 rounded-full text-sm font-medium">
                <Icon name="CheckCircle" size={14} />
                Active
              </span>
            </div>
          </div>
          <Button variant="outline" size="sm" iconName="Settings">
            Manage
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Monthly Cost</p>
            <p className="text-2xl font-bold text-foreground">
              ${currentPlanData?.monthlyPrice}
            </p>
          </div>
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Next Billing Date</p>
            <p className="text-lg font-semibold text-foreground">March 3, 2026</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Billing Cycle</p>
            <p className="text-lg font-semibold text-foreground">Monthly</p>
          </div>
        </div>

        {/* Usage Metrics */}
        <div className="border-t border-border pt-6">
          <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Activity" size={18} className="text-accent" />
            Usage This Month
          </h4>
          <div className="space-y-4">
            {/* Job Postings Usage */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">Job Postings</span>
                <span className="text-sm font-semibold text-foreground">
                  {currentUsage?.jobPostings?.used} / {formatLimit(currentUsage?.jobPostings?.limit)}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    getUsageColor(getUsagePercentage(currentUsage?.jobPostings?.used, currentUsage?.jobPostings?.limit))
                  }`}
                  style={{
                    width: `${getUsagePercentage(currentUsage?.jobPostings?.used, currentUsage?.jobPostings?.limit)}%`
                  }}
                />
              </div>
            </div>

            {/* Active Jobs Usage */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">Active Jobs</span>
                <span className="text-sm font-semibold text-foreground">
                  {currentUsage?.activeJobs?.used} / {formatLimit(currentUsage?.activeJobs?.limit)}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    getUsageColor(getUsagePercentage(currentUsage?.activeJobs?.used, currentUsage?.activeJobs?.limit))
                  }`}
                  style={{
                    width: `${getUsagePercentage(currentUsage?.activeJobs?.used, currentUsage?.activeJobs?.limit)}%`
                  }}
                />
              </div>
            </div>

            {/* Candidate Views Usage */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">Candidate Views</span>
                <span className="text-sm font-semibold text-foreground">
                  {currentUsage?.candidateViews?.used} / {formatLimit(currentUsage?.candidateViews?.limit)}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    getUsageColor(getUsagePercentage(currentUsage?.candidateViews?.used, currentUsage?.candidateViews?.limit))
                  }`}
                  style={{
                    width: `${getUsagePercentage(currentUsage?.candidateViews?.used, currentUsage?.candidateViews?.limit)}%`
                  }}
                />
              </div>
            </div>
          </div>

          {getUsagePercentage(currentUsage?.jobPostings?.used, currentUsage?.jobPostings?.limit) >= 70 && (
            <div className="mt-4 bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">
                  Approaching Usage Limit
                </p>
                <p className="text-sm text-muted-foreground mb-3">
                  You're using {getUsagePercentage(currentUsage?.jobPostings?.used, currentUsage?.jobPostings?.limit)?.toFixed(0)}% of your job posting limit. Consider upgrading for unlimited access.
                </p>
                <Button variant="warning" size="sm" iconName="ArrowUp">
                  Upgrade Plan
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Payment Methods */}
      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
            <Icon name="CreditCard" size={20} className="text-accent" />
            Payment Methods
          </h3>
          <Button variant="outline" size="sm" iconName="Plus">
            Add Method
          </Button>
        </div>
        <div className="space-y-3">
          {paymentMethods?.map((method) => (
            <div
              key={method?.id}
              className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="CreditCard" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">
                    {method?.brand} •••• {method?.last4}
                  </p>
                  <p className="text-sm text-muted-foreground">Expires {method?.expiry}</p>
                </div>
              </div>
              {method?.isDefault && (
                <span className="inline-flex items-center gap-1 bg-success/10 text-success px-2 py-1 rounded text-xs font-medium">
                  <Icon name="Check" size={12} />
                  Default
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Billing History */}
      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-accent" />
            Billing History
          </h3>
          <Button variant="ghost" size="sm" iconName="Download">
            Export All
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                  Invoice
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                  Amount
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                  Status
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {billingHistory?.map((item) => (
                <tr key={item?.id} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                  <td className="py-3 px-4 text-sm text-foreground">
                    {new Date(item.date)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground font-mono">
                    {item?.invoice}
                  </td>
                  <td className="py-3 px-4 text-sm font-semibold text-foreground">
                    ${item?.amount?.toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1 bg-success/10 text-success px-2 py-1 rounded text-xs font-medium">
                      <Icon name="CheckCircle" size={12} />
                      {item?.status?.charAt(0)?.toUpperCase() + item?.status?.slice(1)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button variant="ghost" size="xs" iconName="Download">
                      Download
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Upgrade/Downgrade Options */}
      <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-xl p-6 border border-accent/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Zap" size={24} className="text-accent" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-heading font-bold text-foreground mb-2">
              Ready to Unlock More Features?
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upgrade to Professional or Enterprise for unlimited job postings, advanced analytics, and priority support.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button variant="default" size="sm" iconName="ArrowUp">
                Upgrade Now
              </Button>
              <Button variant="outline" size="sm" iconName="MessageCircle">
                Talk to Sales
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingManagement;